import * as THREE from "./humanodoro-three.module.js";
import { GLTFLoader } from "./humanodoro-three-GLTFLoader.js";
import { OrbitControls } from "./humanodoro-three-OrbitControls.js";

const DEFAULT_ASSETS = {
  modelUrl: "./humanodoro-b2b-pad.glb",
  corkMap: "./humanodoro-b2b-cork-diff.jpg",
  corkNormal: "./humanodoro-b2b-cork-nor.jpg",
  corkRoughness: "./humanodoro-b2b-cork-rough.jpg",
  woodMap: "./humanodoro-b2b-wood-diff.jpg",
  woodNormal: "./humanodoro-b2b-wood-nor.jpg",
  woodRoughness: "./humanodoro-b2b-wood-rough.jpg",
};

function resolveAssets() {
  return Object.assign({}, DEFAULT_ASSETS, window.HumanodoroB2BThreeAssets || {});
}

function makeMaterialSet(renderer, assets) {
  const loader = new THREE.TextureLoader();
  const textureNames = ["corkMap", "corkNormal", "corkRoughness", "woodMap", "woodNormal", "woodRoughness"];
  const textures = {};

  textureNames.forEach((name) => {
    textures[name] = loader.load(assets[name]);
    textures[name].wrapS = THREE.RepeatWrapping;
    textures[name].wrapT = THREE.RepeatWrapping;
    textures[name].anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  });

  textures.corkMap.colorSpace = THREE.SRGBColorSpace;
  textures.woodMap.colorSpace = THREE.SRGBColorSpace;

  return {
    corkMat: new THREE.MeshStandardMaterial({
      map: textures.corkMap,
      normalMap: textures.corkNormal,
      roughnessMap: textures.corkRoughness,
      roughness: 1,
      metalness: 0,
    }),
    woodMat: new THREE.MeshStandardMaterial({
      map: textures.woodMap,
      normalMap: textures.woodNormal,
      normalScale: new THREE.Vector2(0.6, 0.6),
      roughnessMap: textures.woodRoughness,
      roughness: 1,
      metalness: 0,
      color: "#ecd3ad",
    }),
  };
}

class HumanodoroB2BThreeViewer {
  constructor(canvas) {
    this.canvas = canvas;
    this.assets = resolveAssets();
    this.logoRotation = 0;
    this.logoMesh = null;
    this.padBounds = null;
    this.ready = false;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.shadowMap.enabled = true;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.01, 100);
    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enablePan = false;
    this.controls.enableZoom = true;
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.addEventListener("change", () => this.render());

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.62));
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0xffde9c, 0.28));
    const mainLight = new THREE.DirectionalLight(0xffffff, 1.05);
    mainLight.position.set(5, 6, 4);
    mainLight.castShadow = true;
    mainLight.shadow.mapSize.width = 1024;
    mainLight.shadow.mapSize.height = 1024;
    this.scene.add(mainLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.35);
    fillLight.position.set(-4, 3, -4);
    this.scene.add(fillLight);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    window.addEventListener("resize", () => this.resize());

    this.materials = makeMaterialSet(this.renderer, this.assets);
    this.loadModel();
    this.animate();
  }

  loadModel() {
    const loader = new GLTFLoader();
    loader.load(
      this.assets.modelUrl,
      (gltf) => {
        this.model = gltf.scene;
        const box = new THREE.Box3();
        this.model.traverse((obj) => {
          if (!obj.isMesh) return;
          if (/^plane/i.test(obj.name)) {
            obj.visible = false;
            return;
          }
          obj.material = /cork/i.test(obj.name) ? this.materials.corkMat : this.materials.woodMat;
          obj.castShadow = true;
          obj.receiveShadow = true;
          box.expandByObject(obj);
        });

        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        this.padBounds = { size, center };
        this.model.position.sub(center);
        this.scene.add(this.model);
        this.fitCamera(size);
        this.ready = true;
        this.resize();
        if (this.pendingLogo) {
          this.setLogo(this.pendingLogo.dataUrl, this.pendingLogo.rotationDeg, this.pendingLogo.width, this.pendingLogo.height);
          this.pendingLogo = null;
        }
        this.render();
      },
      undefined,
      (error) => {
        console.warn("[humanodoro-b2b] 3D pad failed to load", error);
        this.canvas.dispatchEvent(new CustomEvent("humanodoro:b2b-three-error"));
      },
    );
  }

  fitCamera(size) {
    const maxDim = Math.max(size.x, size.y, size.z);
    const distance = (maxDim / (2 * Math.tan((this.camera.fov * Math.PI) / 360))) * 1.25;
    const direction = new THREE.Vector3(3, 3, 4).normalize();
    this.camera.position.copy(direction.multiplyScalar(distance));
    this.camera.near = Math.max(0.01, distance / 100);
    this.camera.far = distance * 100;
    this.camera.updateProjectionMatrix();
    this.controls.target.set(0, 0, 0);
    this.controls.update();
  }

  resize() {
    const rect = this.canvas.getBoundingClientRect();
    const width = Math.max(320, Math.round(rect.width));
    const height = Math.max(320, Math.round(rect.height));
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.render();
  }

  animate() {
    this.animationFrame = window.requestAnimationFrame(() => this.animate());
    this.controls.update();
    this.render();
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }

  setLogo(dataUrl, rotationDeg, width, height) {
    if (!this.ready || !this.padBounds) {
      this.pendingLogo = { dataUrl, rotationDeg, width, height };
      return;
    }

    this.clearLogo();
    this.logoRotation = rotationDeg || 0;
    const texture = new THREE.TextureLoader().load(dataUrl, () => this.render());
    texture.colorSpace = THREE.SRGBColorSpace;

    const padTopW = this.padBounds.size.x;
    const padTopD = this.padBounds.size.z;
    const swapped = this.logoRotation % 180 !== 0;
    const fitW = (swapped ? padTopD : padTopW) * 0.4;
    const fitD = (swapped ? padTopW : padTopD) * 0.4;
    const image = texture.image || {};
    const imageWidth = width || image.width || 2;
    const imageHeight = height || image.height || 1;
    const aspect = imageWidth && imageHeight ? imageWidth / imageHeight : 2;
    let logoW = fitW;
    let logoH = fitW / aspect;
    if (logoH > fitD) {
      logoH = fitD;
      logoW = logoH * aspect;
    }

    const topY = this.padBounds.size.y / 2 + 0.002;
    const material = new THREE.MeshStandardMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      color: "#4a2c10",
      opacity: 0.55,
      roughness: 1,
      metalness: 0,
    });
    this.logoMesh = new THREE.Mesh(new THREE.PlaneGeometry(logoW, logoH), material);
    this.logoMesh.position.set(0, topY, 0);
    this.logoMesh.rotation.set(-Math.PI / 2, 0, -(this.logoRotation * Math.PI) / 180);
    this.logoMesh.renderOrder = 1;
    this.scene.add(this.logoMesh);
    this.render();
  }

  clearLogo() {
    if (!this.logoMesh) return;
    this.scene.remove(this.logoMesh);
    this.logoMesh.geometry.dispose();
    this.logoMesh.material.map?.dispose();
    this.logoMesh.material.dispose();
    this.logoMesh = null;
    this.render();
  }

  setLogoRotation(rotationDeg) {
    this.logoRotation = rotationDeg || 0;
    if (this.logoMesh) {
      this.logoMesh.rotation.set(-Math.PI / 2, 0, -(this.logoRotation * Math.PI) / 180);
      this.render();
    }
  }
}

window.HumanodoroB2BThree = HumanodoroB2BThreeViewer;
window.dispatchEvent(new CustomEvent("humanodoro:b2b-three-ready"));
